import { useMemo } from 'react';
import { PhoneAnalyticsTypes } from '@frontend/api-analytics';
import { AnalyticsUtils, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { BarChartData, Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { useFetchAggregatedCallsAndDurationReport } from '../../hooks/use-fetch-aggregated-calls-and-duration-report';
import { usePhoneAnalyticsShallowStore } from '../../hooks/use-phone-analytics-store';
import { ChartProps } from './shared-types';

const colors = {
  incoming: theme.colors.secondary.seaweed30,
  outgoing: theme.colors.warning50,
};

const labels = {
  incoming: i18next.t('Incoming', { ns: 'analytics-phone' }),
  outgoing: i18next.t('Outgoing', { ns: 'analytics-phone' }),
};

type ProcessedTotals = {
  calls: {
    inbound: number;
    outbound: number;
  };
  duration: {
    inbound: number;
    outbound: number;
  };
};

type ProcessedData = {
  chartData: BarChartData;
  average: {
    incoming: number;
    outgoing: number;
  };
};

export const AverageCallDurationChart = ({ defaultChartAppearance = {}, exportPdfProps, isSmallMode }: ChartProps) => {
  const { t } = useTranslation('analytics-phone');
  const { accessibleLocationsNames, filterHintText, isDemoAccount } = usePhoneAnalyticsShallowStore(
    'accessibleLocationsNames',
    'filterHintText',
    'isDemoAccount'
  );
  const { data, isHourlyData, isLoading, isMultiLocation } =
    useFetchAggregatedCallsAndDurationReport<PhoneAnalyticsTypes.CallsAggregationKey>('call_direction');

  const processeData: ProcessedData = useMemo(() => {
    let groups: BarChartData['groups'] = [];

    if (isMultiLocation) {
      // There will be multiple location objects with single/multiple dates
      groups = Object.entries(data?.aggregatedMultiLocations || {}).map(([key, { calls, duration }]) => {
        const averageIncoming = (duration?.inbound || 0) / (calls?.inbound || 1);
        const averageOutgoing = (duration?.outbound || 0) / (calls?.outbound || 1);

        return {
          name: accessibleLocationsNames?.[key] || key,
          values: {
            incoming: averageIncoming,
            outgoing: averageOutgoing,
          },
        };
      });
    } else {
      // For hourly data: There will be only one object with single date and hourly data for the given location
      // For daily data: There will be only one object with multiple dates for the given location
      const locationData = Object.values(data?.locations || {})[0] || {};

      if (isHourlyData) {
        // Hourly data is restricted to a single date, hence only one object
        const { inbound, outbound } = Object.values(locationData || {})[0] || {};
        const limitedIncomingCalls = inbound?.calls?.hourly || [];
        const limitedIncomingDuration = inbound?.duration?.hourly || [];
        const limitedOutgoingCalls = outbound?.calls?.hourly || [];
        const limitedOutgoingDuration = outbound?.duration?.hourly || [];

        groups = limitedIncomingCalls.map((incomingCalls, i) => {
          const averageIncoming = (limitedIncomingDuration[i] || 0) / (incomingCalls || 1);
          const averageOutgoing = (limitedOutgoingDuration[i] || 0) / (limitedOutgoingCalls[i] || 1);

          return {
            name: AnalyticsUtils.getHourlyInsightsLabel(i),
            values: {
              incoming: averageIncoming,
              outgoing: averageOutgoing,
            },
          };
        });
      } else {
        groups = Object.entries(locationData).map(([date, { inbound, outbound }]) => {
          const averageIncoming = (inbound?.duration?.daily || 0) / (inbound?.calls?.daily || 1);
          const averageOutgoing = (outbound?.duration?.daily || 0) / (outbound?.calls?.daily || 1);

          return {
            name: formatters.date(date),
            values: {
              incoming: averageIncoming,
              outgoing: averageOutgoing,
            },
          };
        });
      }
    }

    const totals = Object.values(data?.aggregatedMultiLocations ?? {}).reduce<ProcessedTotals>(
      (acc, { calls, duration }) => {
        acc.calls.inbound = (acc.calls?.inbound || 0) + (calls?.inbound || 0);
        acc.calls.outbound = (acc.calls?.outbound || 0) + (calls?.outbound || 0);
        acc.duration.inbound = (acc.duration?.inbound || 0) + (duration?.inbound || 0);
        acc.duration.outbound = (acc.duration?.outbound || 0) + (duration?.outbound || 0);
        return acc;
      },
      {
        calls: {
          inbound: 0,
          outbound: 0,
        },
        duration: {
          inbound: 0,
          outbound: 0,
        },
      }
    );

    return {
      average: {
        incoming: (totals.duration.inbound || 0) / (totals.calls.inbound || 1),
        outgoing: (totals.duration.outbound || 0) / (totals.calls.outbound || 1),
      },
      chartData: {
        groups,
      },
    };
  }, [accessibleLocationsNames, data, isHourlyData, isMultiLocation]);

  return (
    <Chart colors={colors} isLoading={isLoading} labels={labels}>
      <Chart.Header
        {...exportPdfProps}
        infoTip={
          <InfoTipPopover>
            {t(
              'Incoming vs Outgoing: Refers to the average duration of the incoming and outgoing calls. Value is in hh:mm:ss'
            )}
          </InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
        subtitle={!isSmallMode && filterHintText}
        title={t('Average Call Duration')}
      />
      <Chart.BarChart
        appearance={defaultChartAppearance}
        data={processeData.chartData}
        formatValue={(value) => AnalyticsUtils.toHHmmss(value)}
      />
      <Chart.Legends formatValue={(value) => AnalyticsUtils.toHHmmss(value)} values={processeData.average} />
    </Chart>
  );
};
